import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, ArrowRight, Zap, Crown, Flame, Moon, RotateCw, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { setOnboardedCache } from "@/components/RequireAuth";
import { StitchPage, StitchPanel } from "@/components/stitch";
import { motion, useReducedMotion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { isNativeApp } from "@/lib/platform";
import NativeOnboardingIntro from "@/pages/auth/NativeOnboardingIntro";

// v2 — bumped after adding the testimonial slide so existing native users
// who already dismissed the v1 tour get the new tour once on next open.
const INTRO_SEEN_KEY = "fitaura.onboarding.intro.seen.v2";

type Aura = {
  id: string;
  name: string;
  tagline: string;
  Icon: typeof Zap;
  seedStyles: string[];
  seedColors: string[];
};

const AURAS: Aura[] = [
  {
    id: "neon-muse",
    name: "Neon Muse",
    tagline: "Bold party energy. Saturated palettes, statement fits.",
    Icon: Zap,
    seedStyles: ["Party", "Streetwear"],
    seedColors: ["Bright", "Jewel"],
  },
  {
    id: "quiet-luxe",
    name: "Quiet Luxe",
    tagline: "Tailored minimalism. Quality over noise.",
    Icon: Crown,
    seedStyles: ["Formal", "Minimal"],
    seedColors: ["Neutral", "Monochrome"],
  },
  {
    id: "street-oracle",
    name: "Street Oracle",
    tagline: "Urban edge. Layers, sneakers, oversized cuts.",
    Icon: Flame,
    seedStyles: ["Streetwear", "Casual"],
    seedColors: ["Black", "Earth"],
  },
  {
    id: "studio-soft",
    name: "Studio Soft",
    tagline: "Calm, romantic, light. Pastels and easy silhouettes.",
    Icon: Moon,
    seedStyles: ["Boho", "Minimal"],
    seedColors: ["Pastel", "White"],
  },
];

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const BODY_TYPES = ["Slim", "Athletic", "Average", "Curvy", "Plus"];
const SKIN_TONES = ["Fair", "Light", "Medium", "Tan", "Brown", "Deep"];
const STYLES = ["Casual", "Streetwear", "Formal", "Party", "Sporty", "Minimal", "Boho", "Vintage"];
const COLORS = ["Black", "White", "Neutral", "Pastel", "Bright", "Earth", "Jewel", "Monochrome"];

const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-4 py-2 text-sm font-medium transition-all",
      active
        ? "border-primary bg-primary text-primary-foreground shadow-[0_0_24px_-6px_hsl(var(--primary))]"
        : "border-white/15 bg-white/[0.03] text-foreground hover:border-primary/50 hover:bg-white/[0.06]",
    )}
  >
    {children}
  </button>
);

const Onboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Native-only feature-tour intro shown ONCE before the calibration wizard.
  // Web users skip straight to the wizard so the desktop experience is untouched.
  const [showIntro, setShowIntro] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    if (!isNativeApp()) return false;
    try {
      return window.localStorage.getItem(INTRO_SEEN_KEY) !== "1";
    } catch {
      return true;
    }
  });
  const dismissIntro = () => {
    try {
      window.localStorage.setItem(INTRO_SEEN_KEY, "1");
    } catch {
      /* private mode — non-fatal */
    }
    setShowIntro(false);
  };
  // Persist step to sessionStorage so a remount during the save→navigate
  // hand-off (e.g. RequireAuth re-evaluating profile state) doesn't snap the
  // panel back to step 0 and cause a visible flicker/refresh.
  const STEP_KEY = "fitaura.onboarding.step";
  const [step, setStep] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    const raw = window.sessionStorage.getItem(STEP_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) && parsed >= 0 && parsed < 4 ? parsed : 0;
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(STEP_KEY, String(step));
    } catch {
      /* storage may be unavailable (private mode) — non-fatal */
    }
  }, [step]);

  const [saving, setSaving] = useState(false);
  // Keep the success state distinct from `saving` so we can hold the
  // "completing" UI through the navigation transition (prevents step-0 flash).
  const [completed, setCompleted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  type Diag = {
    stage:
      | "idle"
      | "lookup"
      | "writing"
      | "writing-insert"
      | "writing-update"
      | "navigating"
      | "done"
      | "timeout"
      | "error";
    startedAt: number;
    elapsedMs: number;
    httpStatus?: number;
    pgCode?: string;
    mode?: "insert" | "update";
    online: boolean;
    message?: string;
  };
  const [diag, setDiag] = useState<Diag | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [aura, setAura] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [bodyType, setBodyType] = useState<string>("");
  const [skinTone, setSkinTone] = useState<string>("");
  const [heightCm, setHeightCm] = useState<string>("");
  const [size, setSize] = useState<string>("");
  const [styles, setStyles] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  const pickAura = (a: Aura) => {
    setAura(a.id);
    // Seed the user's preferences with sensible defaults from the chosen Aura.
    setStyles((prev) => Array.from(new Set([...prev, ...a.seedStyles])));
    setColors((prev) => Array.from(new Set([...prev, ...a.seedColors])));
  };

  // Hydrate form once on mount. We intentionally depend only on the user id
  // (not the user object reference) so a token refresh / context re-render
  // doesn't refetch the profile and overwrite in-progress edits — which was
  // bouncing the user back to step 1 mid-flow.
  const userId = user?.id;
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!userId) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        // If the profile is already onboarded we DO NOT navigate from here —
        // RequireAuth(redirectIfOnboarded) handles that declaratively. Doing
        // it in this effect raced with RequireAuth and produced a redirect
        // ping-pong between /onboarding and /app.
        if (data.onboarded) {
          setOnboardedCache(userId, true);
          return;
        }
        setDisplayName(data.display_name ?? "");
        setGender(data.gender ?? "");
        setBodyType(data.body_type ?? "");
        setSkinTone(data.skin_tone ?? "");
        setHeightCm(data.height_cm ? String(data.height_cm) : "");
        setSize(data.size ?? "");
        setStyles(data.favorite_styles ?? []);
        setColors(data.color_preferences ?? []);
      });
  }, [userId]);

  const toggle = (arr: string[], v: string, setter: (s: string[]) => void) => {
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  // Wrap a promise with a timeout so a hung network request doesn't leave the
  // user staring at a spinner forever — they'll get a clear error + retry CTA.
  const withTimeout = <T,>(p: PromiseLike<T>, ms: number, label: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const t = setTimeout(() => {
        const err = new Error(`${label} timed out after ${Math.round(ms / 1000)}s. Check your connection and try again.`);
        (err as Error & { code?: string }).code = "TIMEOUT";
        reject(err);
      }, ms);
      Promise.resolve(p).then(
        (v) => {
          clearTimeout(t);
          resolve(v);
        },
        (e) => {
          clearTimeout(t);
          reject(e);
        },
      );
    });

  const save = async () => {
    const startedAt = performance.now();
    const online = typeof navigator !== "undefined" ? navigator.onLine : true;
    const nextAttempt = attempt + 1;
    console.log("[Onboarding] ▶ save() start", {
      userId: user?.id,
      step,
      attempt: nextAttempt,
      online,
      payloadPreview: {
        displayName: displayName.trim() || null,
        gender,
        bodyType,
        skinTone,
        heightCm,
        size,
        stylesCount: styles.length,
        colorsCount: colors.length,
      },
    });
    if (!user) {
      toast({ title: "Not signed in", description: "Please sign in again.", variant: "destructive" });
      navigate("/auth", { replace: true });
      return;
    }
    setSaving(true);
    setSaveError(null);
    setAttempt(nextAttempt);
    setDiag({ stage: "lookup", startedAt, elapsedMs: 0, online });

    const elapsed = () => Math.round(performance.now() - startedAt);
    const updateDiag = (patch: Partial<Diag>) =>
      setDiag((d) => ({ ...(d as Diag), ...patch, elapsedMs: elapsed() }));

    try {
      // Stage 1: lookup
      console.log("[Onboarding] ⤷ stage=lookup", { elapsedMs: elapsed() });
      const { data: existing, error: selectErr } = await withTimeout(
        supabase.from("profiles").select("user_id").eq("user_id", user.id).maybeSingle(),
        15000,
        "Profile lookup",
      );
      console.log("[Onboarding] ⤷ lookup done", { existing: !!existing, selectErr, elapsedMs: elapsed() });

      if (selectErr) {
        const msg = selectErr.message || "Couldn't load your profile.";
        updateDiag({ stage: "error", message: msg, pgCode: (selectErr as { code?: string }).code });
        setSaveError(msg);
        toast({ title: "Couldn't load profile", description: msg, variant: "destructive" });
        return;
      }

      const payload = {
        display_name: displayName.trim() || null,
        gender: gender || null,
        body_type: bodyType || null,
        skin_tone: skinTone || null,
        height_cm: heightCm ? Number(heightCm) : null,
        size: size || null,
        favorite_styles: styles,
        color_preferences: colors,
        onboarded: true,
      };

      // Stage 2: write
      const mode: "insert" | "update" = existing ? "update" : "insert";
      updateDiag({ stage: mode === "insert" ? "writing-insert" : "writing-update", mode });
      console.log("[Onboarding] ⤷ stage=writing", { mode, elapsedMs: elapsed() });

      const writeOp =
        mode === "insert"
          ? supabase.from("profiles").insert({ user_id: user.id, ...payload })
          : supabase.from("profiles").update(payload).eq("user_id", user.id);
      const { error, status, statusText } = await withTimeout(writeOp, 15000, "Saving profile");
      console.log("[Onboarding] ⤷ write done", { mode, error, status, statusText, elapsedMs: elapsed() });

      if (error) {
        const pgCode = (error as { code?: string }).code;
        const msg = error.message || "Please try again.";
        console.error("[Onboarding] ✖ save error", { error, pgCode, status, statusText });
        updateDiag({ stage: "error", message: msg, httpStatus: status, pgCode });
        setSaveError(msg);
        toast({ title: "Couldn't save profile", description: msg, variant: "destructive" });
        return;
      }

      updateDiag({ stage: "navigating", httpStatus: status });
      setOnboardedCache(user.id, true);
      setCompleted(true);
      console.log("[Onboarding] ✓ profile saved; navigating to /app/style", {
        userId: user.id,
        totalMs: elapsed(),
        status,
      });
      navigate("/app/style", { replace: true });
    } catch (e) {
      const code = (e as Error & { code?: string }).code;
      const msg = e instanceof Error ? e.message : "Please try again.";
      console.error("[Onboarding] ✖ save exception", { code, msg, elapsedMs: elapsed(), online });
      updateDiag({
        stage: code === "TIMEOUT" ? "timeout" : "error",
        message: msg,
      });
      setSaveError(msg);
      toast({
        title: code === "TIMEOUT" ? "Request timed out" : "Something went wrong",
        description: msg,
        variant: "destructive",
      });
    } finally {
      // Only clear the spinner if we did NOT succeed — on success we keep
      // the "completing" UI mounted until navigation lands.
      setCompleted((c) => {
        if (!c) setSaving(false);
        return c;
      });
    }
  };

  const steps = [
    {
      title: "Choose your aura",
      blurb: "Pick the energy you want the AI to channel. You can refine later.",
      canContinue: !!aura,
      content: (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {AURAS.map((a) => {
            const active = aura === a.id;
            const Icon = a.Icon;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => pickAura(a)}
                className={cn(
                  "group relative flex flex-col items-start gap-2 overflow-hidden rounded-2xl border p-4 text-left transition-all",
                  active
                    ? "border-primary bg-primary/[0.08] shadow-[0_0_36px_-10px_hsl(var(--primary))]"
                    : "border-white/10 bg-white/[0.03] hover:border-primary/40 hover:bg-white/[0.06]",
                )}
                aria-pressed={active}
              >
                {active && (
                  <>
                    <span className="pointer-events-none absolute left-1.5 top-1.5 h-2.5 w-2.5 border-l-2 border-t-2 border-primary" />
                    <span className="pointer-events-none absolute right-1.5 top-1.5 h-2.5 w-2.5 border-r-2 border-t-2 border-primary" />
                    <span className="pointer-events-none absolute bottom-1.5 left-1.5 h-2.5 w-2.5 border-b-2 border-l-2 border-primary" />
                    <span className="pointer-events-none absolute bottom-1.5 right-1.5 h-2.5 w-2.5 border-b-2 border-r-2 border-primary" />
                  </>
                )}
                <span
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-xl transition-colors",
                    active ? "bg-primary text-primary-foreground" : "bg-white/[0.06] text-primary",
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <span className="font-display text-sm font-bold uppercase tracking-[0.12em] text-foreground">
                  {a.name}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">{a.tagline}</span>
              </button>
            );
          })}
        </div>
      ),
    },
    {
      title: "Tell us about you",
      content: (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="onboarding-display-name">Display name</Label>
            <Input
              id="onboarding-display-name"
              name="displayName"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={60}
            />
          </div>
          <div className="space-y-2">
            <span className="block text-sm font-medium leading-none">Gender</span>
            <div className="flex flex-wrap gap-2">
              {GENDERS.map((g) => (
                <Pill key={g} active={gender === g} onClick={() => setGender(g)}>{g}</Pill>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Your body",
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <span className="block text-sm font-medium leading-none">Body type</span>
            <div className="flex flex-wrap gap-2">
              {BODY_TYPES.map((b) => (
                <Pill key={b} active={bodyType === b} onClick={() => setBodyType(b)}>{b}</Pill>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <span className="block text-sm font-medium leading-none">Skin tone</span>
            <div className="flex flex-wrap gap-2">
              {SKIN_TONES.map((s) => (
                <Pill key={s} active={skinTone === s} onClick={() => setSkinTone(s)}>{s}</Pill>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="onboarding-height-cm">Height (cm)</Label>
              <Input
                id="onboarding-height-cm"
                name="heightCm"
                autoComplete="off"
                type="number"
                inputMode="numeric"
                value={heightCm}
                onChange={(e) => setHeightCm(e.target.value)}
                min={100}
                max={230}
                placeholder="170"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="onboarding-size">Size</Label>
              <Input
                id="onboarding-size"
                name="size"
                autoComplete="off"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="M"
                maxLength={6}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Your style",
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <span className="block text-sm font-medium leading-none">Favorite styles</span>
            <div className="flex flex-wrap gap-2">
              {STYLES.map((s) => (
                <Pill key={s} active={styles.includes(s)} onClick={() => toggle(styles, s, setStyles)}>{s}</Pill>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <span className="block text-sm font-medium leading-none">Color preferences</span>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <Pill key={c} active={colors.includes(c)} onClick={() => toggle(colors, c, setColors)}>{c}</Pill>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const isLast = step === steps.length - 1;
  const stepLabel = `STEP 0${step + 1} / 0${steps.length}`;
  const reduceMotion = useReducedMotion();

  // Scroll to top whenever the step changes so the panel is always in view
  // (prevents the "stuck blank panel" bug when later steps render taller).
  // Skip during save/completing so a brief remount doesn't snap scroll and
  // create the visible flicker the user reported on success.
  useEffect(() => {
    if (saving || completed) return;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step, saving, completed]);

  // Clear the persisted step on successful completion so a fresh signup
  // doesn't resume mid-flow.
  useEffect(() => {
    if (completed) {
      try {
        window.sessionStorage.removeItem(STEP_KEY);
      } catch {
        /* non-fatal */
      }
    }
  }, [completed]);

  if (showIntro) {
    return (
      <>
        <SEO noindex />
        <NativeOnboardingIntro onComplete={dismissIntro} />
      </>
    );
  }

  return (
    <>
      <SEO noindex />
    <StitchPage>
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-5 py-6" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}>
        {/* Proper top header bar — brand, step count, and a back affordance
            so users always know where they are in the calibration flow. */}
        <header className="mb-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)}
            aria-label={step > 0 ? "Previous step" : "Back"}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-foreground/80 transition hover:border-primary/40 hover:text-primary disabled:opacity-30"
            disabled={saving || completed}
          >
            <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-[0_0_24px_-4px_hsl(var(--primary))]">
              <Sparkles className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-[13px] font-bold uppercase tracking-[0.2em] text-foreground">
              FitAura AI
            </span>
          </div>
          <span className="font-display min-w-[40px] text-right text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
            {String(step + 1).padStart(2, "0")}/{String(steps.length).padStart(2, "0")}
          </span>
        </header>

        <div className="mb-3 flex items-center justify-between">
          <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
            — {stepLabel}
          </span>
          <span className="font-display text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
            Calibration
          </span>
        </div>

        <div className="mb-6 flex gap-1.5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full transition-all",
                i <= step
                  ? "bg-primary shadow-[0_0_12px_-2px_hsl(var(--primary))]"
                  : "bg-white/10",
              )}
            />
          ))}
        </div>

        <motion.div
          key={step}
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <StitchPanel className="flex-1 space-y-6 p-6" glow>
            <div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-foreground">
                {steps[step].title}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {steps[step].blurb ??
                  (step === 1
                    ? "Tell the AI who it's styling."
                    : step === 2
                      ? "Helps the AI nail proportions and tone."
                      : "We tune every recommendation around this.")}
              </p>
            </div>
            {steps[step].content}
          </StitchPanel>
        </motion.div>

        {/* Inline diagnostics + retry on the final step. We surface the exact
            stage, elapsed time, HTTP/Postgres codes and online status so any
            stuck flow is immediately explainable instead of a silent spinner. */}
        {isLast && (saving || saveError || diag) && (
          <div
            role={saveError ? "alert" : "status"}
            aria-live="polite"
            className={cn(
              "mt-4 flex items-start gap-3 rounded-xl border p-3 text-xs",
              saveError
                ? "border-destructive/40 bg-destructive/10"
                : "border-primary/30 bg-primary/[0.06]",
            )}
          >
            {saveError ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={2.5} />
            ) : (
              <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" strokeWidth={2.5} />
            )}
            <div className="flex-1 space-y-1">
              <p className="font-semibold text-foreground">
                {saveError
                  ? diag?.stage === "timeout"
                    ? "Scan timed out"
                    : "We couldn't finish your scan"
                  : completed
                    ? "Scan complete — entering the runway…"
                    : diag?.stage === "lookup"
                      ? "Reading your profile…"
                      : diag?.stage === "writing-insert"
                        ? "Creating your profile…"
                        : diag?.stage === "writing-update"
                          ? "Saving your preferences…"
                          : diag?.stage === "navigating"
                            ? "Almost there…"
                            : "Working…"}
              </p>
              {saveError && <p className="text-muted-foreground">{saveError}</p>}
              {diag && (
                <p className="font-mono text-[10px] text-muted-foreground/80">
                  stage={diag.stage} · {diag.elapsedMs}ms · attempt {attempt}
                  {diag.httpStatus ? ` · http ${diag.httpStatus}` : ""}
                  {diag.pgCode ? ` · pg ${diag.pgCode}` : ""}
                  {diag.mode ? ` · ${diag.mode}` : ""}
                  {!diag.online ? " · offline" : ""}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              className="flex-1 border-white/15 bg-white/[0.03] hover:bg-white/[0.07]"
              onClick={() => setStep(step - 1)}
              disabled={saving || completed}
            >
              Back
            </Button>
          )}
          {!isLast ? (
            <Button
              className="font-display flex-1 bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))] hover:opacity-95 disabled:opacity-40"
              onClick={() => setStep(step + 1)}
              disabled={steps[step].canContinue === false}
            >
              Continue
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : saveError && !saving ? (
            <Button
              className="font-display flex-1 bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))] hover:opacity-95"
              onClick={save}
            >
              <RotateCw className="mr-2 h-4 w-4" />
              {diag?.stage === "timeout" ? "Retry (timed out)" : "Retry scan"}
            </Button>
          ) : (
            <Button
              className="font-display flex-1 bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))] hover:opacity-95"
              onClick={save}
              disabled={saving || completed}
            >
              {(saving || completed) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {completed ? "Entering…" : saving ? "Scanning…" : "Enter the Runway"}
            </Button>
          )}
        </div>
      </div>
    </StitchPage>
    </>
  );
};

export default Onboarding;
